import React from 'react';

type SparklineProps = {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  strokeWidth?: number;
  showBaseline?: boolean;
  rounded?: boolean;
  className?: string;
};

/**
 * Lightweight Sparkline (no external deps)
 * - Normalizes data to fit viewBox
 * - Renders a path line and optional baseline
 */
const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 160,
  height = 40,
  stroke = '#22c55e',
  fill,
  strokeWidth = 2,
  showBaseline = false,
  rounded = true,
  className,
}) => {
  if (!data || data.length === 0) {
    return (
      <svg width={width} height={height} className={className}>
        <rect x="0" y={height / 2 - 1} width={width} height={2} fill="#334155" />
      </svg>
    );
  }

  // Guard against all-zero arrays to avoid division by zero
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = Math.max(max - min, 1);

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (width - 2) + 1; // padding 1px
    const norm = (v - min) / range;
    const y = height - 1 - norm * (height - 2); // invert y, padding 1px
    return [x, y] as const;
  });

  const pathD = points
    .map(([x, y], i) => (i === 0 ? `M ${x},${y}` : `L ${x},${y}`))
    .join(' ');

  // Optionally create a filled area under the sparkline
  const areaD = `${pathD} L ${points[points.length - 1][0]},${height - 1} L ${points[0][0]},${height - 1} Z`;

  return (
    <svg width={width} height={height} className={className}>
      {showBaseline && (
        <line
          x1={0}
          y1={height - 1}
          x2={width}
          y2={height - 1}
          stroke="#334155"
          strokeWidth={1}
        />
      )}
      {fill && (
        <path d={areaD} fill={fill} opacity={0.15} />
      )}
      <path
        d={pathD}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap={rounded ? 'round' : 'butt'}
        strokeLinejoin={rounded ? 'round' : 'miter'}
      />
    </svg>
  );
};

export default Sparkline;
